import { useState, useEffect, useCallback } from 'react';
import api from '../../api/client';
import toast from 'react-hot-toast';
import StaffLink from '../../components/shared/StaffLink';
import {
  CalendarDaysIcon, CheckCircleIcon, XCircleIcon, ClockIcon,
} from '@heroicons/react/24/outline';

const WEEKDAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];
const labelize = (k) => k[0].toUpperCase() + k.slice(1);

const formatPattern = (type, pattern) => {
  if (type === 'prn') return 'PRN / float (days vary week-to-week)';
  if (!pattern || typeof pattern !== 'object') return 'No pattern set';
  const parts = WEEKDAYS.map(d => {
    const v = pattern[d];
    if (!v || v === 'unavailable') return `${labelize(d)}: off`;
    if (typeof v === 'object' && v.start && v.end) return `${labelize(d)}: ${v.start}–${v.end}`;
    return `${labelize(d)}: ?`;
  });
  return parts.join(' · ');
};

export default function AvailabilityRequestsPage() {
  const [tab, setTab] = useState('pending');
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deciding, setDeciding] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/availability-requests?status=${tab}`);
      setRequests(data || []);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  const decide = async (id, decision, decision_notes) => {
    setDeciding(d => ({ ...d, [id]: true }));
    try {
      await api.patch(`/availability-requests/${id}`, { decision, decision_notes });
      toast.success(decision === 'approved' ? 'Approved & saved' : 'Denied');
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally {
      setDeciding(d => ({ ...d, [id]: false }));
    }
  };

  return (
    <div className="animate-slide-up">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <CalendarDaysIcon className="w-6 h-6" /> Availability Change Requests
        </h1>
        <p className="text-gray-500 text-sm mt-1">
          Staff submit availability changes here for your approval. Approved changes update their
          scheduling profile and the AI scheduler picks them up immediately.
        </p>
      </header>

      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {[
          { id: 'pending', label: 'Pending', icon: ClockIcon },
          { id: 'approved', label: 'Approved', icon: CheckCircleIcon },
          { id: 'denied', label: 'Denied', icon: XCircleIcon },
        ].map(t => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
                tab === t.id ? 'border-orange-500 text-orange-600' : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500" /></div>
      ) : requests.length === 0 ? (
        <div className="bg-white rounded-2xl border border-gray-200 p-8 text-center text-sm text-gray-500">
          No {tab} requests.
        </div>
      ) : (
        <div className="space-y-4">
          {requests.map(r => (
            <RequestCard key={r.id} request={r} onDecide={decide} deciding={!!deciding[r.id]} />
          ))}
        </div>
      )}
    </div>
  );
}

function RequestCard({ request, onDecide, deciding }) {
  const [notes, setNotes] = useState('');

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5">
      <div className="flex items-start justify-between gap-4 mb-3">
        <div>
          <p className="text-sm font-semibold text-gray-900">
            <StaffLink id={request.staff_user_id} name={`${request.first_name} ${request.last_name}`} />
            <span className="text-gray-400 font-normal"> · {request.role?.replace(/_/g, ' ')}</span>
          </p>
          <p className="text-xs text-gray-500 mt-0.5">
            Submitted {new Date(request.requested_at).toLocaleString()}
            {request.effective_date && <> · effective {new Date(request.effective_date + 'T00:00:00').toLocaleDateString()}</>}
          </p>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${
          request.status === 'approved' ? 'bg-green-100 text-green-700' :
          request.status === 'denied' ? 'bg-red-100 text-red-700' :
          'bg-amber-100 text-amber-700'
        }`}>{request.status}</span>
      </div>

      {request.reason && (
        <p className="text-xs text-gray-700 mb-3 bg-gray-50 rounded-lg px-3 py-2">
          <span className="font-semibold">Reason:</span> {request.reason}
        </p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3 text-xs">
        <div className="rounded-lg border border-gray-100 p-3 bg-gray-50">
          <p className="font-semibold text-gray-700 mb-1">Currently</p>
          <p className="text-gray-600">{request.prior_availability_type || 'fixed'}</p>
          <p className="text-gray-500 mt-1 leading-relaxed">{formatPattern(request.prior_availability_type, request.prior_availability_pattern)}</p>
        </div>
        <div className="rounded-lg border border-orange-200 p-3 bg-orange-50/40">
          <p className="font-semibold text-orange-900 mb-1">Proposed</p>
          <p className="text-orange-800">{request.proposed_availability_type}</p>
          <p className="text-orange-700 mt-1 leading-relaxed">{formatPattern(request.proposed_availability_type, request.proposed_availability_pattern)}</p>
        </div>
      </div>

      {request.status === 'pending' && (
        <div className="pt-3 border-t border-gray-100 flex items-end gap-2">
          <div className="flex-1">
            <label className="block text-[11px] font-medium text-gray-600 mb-1">Decision notes (optional)</label>
            <input value={notes} onChange={(e) => setNotes(e.target.value)}
              placeholder="Any context for the staff member…"
              className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm" />
          </div>
          <button onClick={() => onDecide(request.id, 'approved', notes)} disabled={deciding}
            className="bg-green-500 text-white px-4 py-1.5 rounded-xl text-sm font-medium hover:bg-green-600 disabled:opacity-50 inline-flex items-center gap-1">
            <CheckCircleIcon className="w-4 h-4" /> Approve
          </button>
          <button onClick={() => onDecide(request.id, 'denied', notes)} disabled={deciding}
            className="bg-white border border-gray-200 text-gray-700 px-4 py-1.5 rounded-xl text-sm font-medium hover:bg-gray-50 disabled:opacity-50 inline-flex items-center gap-1">
            <XCircleIcon className="w-4 h-4" /> Deny
          </button>
        </div>
      )}

      {request.status !== 'pending' && request.decision_notes && (
        <p className="text-xs text-gray-500 italic mt-2">"{request.decision_notes}"</p>
      )}
    </div>
  );
}
